import { CheckCircleIcon } from '@heroicons/react/solid';
import { Box, Button } from '@mui/material';
import { FastField } from 'formik';
import { useCallback, useState } from 'react';
import useSWR from 'swr';

import { formatCurrency, formatPercentage } from '@shared/commonFunctions';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { LenderSectionRow, LenderSectionRowValidation } from '@styles/ContactManager/DealCalculator/LenderSectionStyles';
import { colorTheme, extraColor } from '@styles/muiTheme';
import { CreditBureauScoreType, CreditBureauScoreTypeMap, EBureauType } from '@modules/ContactManager/components/Header/interfaces';

import { Modal } from '@shared/components/modals/Modal';
import { IMap } from '@shared/interfaces';
import { DetailedCreditReport } from '@modules/ContactManager/components/Header/components/DetailedCreditReport';
import { useQueryState } from '@shared/hooks/useQueryState';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { Roles } from '@shared/constants/constants';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { SERVICES } from '@shared/constants/apiConfig';
import { NContact } from '@modules/ContactManager/components/Tabs/components/Contact/validation';
import { DefaultValuationMethods } from '../../../../interfaces';
import { IEligibleLender } from '../../../../models/EligibleLenderModel';
import { IPrimaryBorrowerBureau } from '../../../../models/PrimaryBorrowerBureauModel';

const HEIGHT_VALUATION_BLOCK = 282;

type ILenderInputsProps = {
  lender: IEligibleLender;
  idx: number;
  valuation: number;
  LTV: () => number;
  maxLtvGreen: number;
  maxLtvYellow: number;
  getColorCoding: (value: number, valueYellow: number, valueGreen: number) => string;
  getDownPaymentColorCoding: (value: number, requiredDownPayment: number) => string;
  isExpanded(section: string): boolean;
  coContactId?: string;
  tierConditionsFirstTermGroup: number;
  maxTermRed: () => string;
  reloadDealCalculator?: () => void;
  reloadContactAlerts: () => void;
};

interface IValuationBlock {
  isExpanded(section: string): boolean;
  defaultValuationMethods: string[];
}

const ValuationBlock = ({ defaultValuationMethods, isExpanded }: IValuationBlock): JSX.Element | null => {
  return isExpanded('valuation') ? <Box height={HEIGHT_VALUATION_BLOCK}>
    {Object.keys(DefaultValuationMethods).map((valuationMethod) => (
      <LenderSectionRow key={valuationMethod}>
        {
          defaultValuationMethods.includes(valuationMethod) &&
          <CheckCircleIcon width={25} color={extraColor.palette.green.main} />
        }
      </LenderSectionRow>
    ))
    }
  </Box> : null;
};

const getBureauMapping = (
  bureau?: string
): { bureauType: EBureauType; ficoType: CreditBureauScoreType } | null => {
  if (!bureau) return null;

  const formattedFicoType = CreditBureauScoreTypeMap[bureau as keyof typeof CreditBureauScoreTypeMap] as unknown as CreditBureauScoreType;

  if (!formattedFicoType) return null;

  const formattedFicoTypeLower = formattedFicoType.toString().toLowerCase();

  if (formattedFicoTypeLower.startsWith('tu')) return { bureauType: EBureauType.Transunion, ficoType: formattedFicoType };
  if (formattedFicoTypeLower.startsWith('eq')) return { bureauType: EBureauType.Equifax, ficoType: formattedFicoType };
  if (formattedFicoTypeLower.startsWith('exp')) return { bureauType: EBureauType.Experian, ficoType: formattedFicoType };

  return null;
};

const LenderInputs = ({
  lender,
  idx,
  valuation,
  LTV,
  maxLtvGreen,
  maxLtvYellow,
  getColorCoding,
  getDownPaymentColorCoding,
  isExpanded,
  coContactId,
  tierConditionsFirstTermGroup,
  maxTermRed,
  reloadDealCalculator,
  reloadContactAlerts,
}: ILenderInputsProps) => {
  const { isAdmin, userHasRoles } = useUserAccount();
  const [contactIdQuery] = useQueryState('id');
  const [creditReports, setCreditReports] = useState<IMap<number>>({});
  const [displayCreditReport, setDisplayCreditReport] = useState(false);
  const [selectedBureauType, setSelectedBureauType] = useState<EBureauType | null>();
  const [pulledFicoTypes, setPulledFicoTypes] = useState<CreditBureauScoreType[]>([]);
  const [contactId, setContactId] = useState(contactIdQuery);

  const canPullAll = isAdmin || userHasRoles([Roles.CREDIT_REPORTS_PULL_ALL]);
  const canPullExperian = userHasRoles([Roles.CREDIT_REPORTS_PULL_EXPERIAN]);
  const canPullTransunion = userHasRoles([Roles.CREDIT_REPORTS_PULL_TRANSUNION]);
  const canPullEquifax = userHasRoles([Roles.CREDIT_REPORTS_PULL_EQUIFAX]);
  const canViewAll = userHasRoles([Roles.CREDIT_REPORTS_VIEW_ALL]);

  const {
    mutate: reloadContact,
  } = useSWR<NContact.Entity>(contactIdQuery && `/${SERVICES.CONTACT}/contacts/${contactIdQuery}`);
  
    const getBorrowerCreditBgColor = useCallback(
      (lender: IEligibleLender, scoreType: 'preferred' | 'default') => {
        const { primaryBorrowerBureau, coBorrowerBureau, tierConditions } = lender;
    
        if (!primaryBorrowerBureau || !tierConditions?.length) return '';
    
        const minFico = Math.min(
          ...tierConditions.map((tier) => tier.minFico ?? 0)
        );
    
        const borrowerPreferred = primaryBorrowerBureau.preferredScore ?? 0;
        const borrowerDefault = primaryBorrowerBureau.defaultScore ?? 0;
        const coBorrowerPreferred = coBorrowerBureau?.preferredScore ?? 0;
    
        const borrowerPreferredFails = borrowerPreferred > 0 && borrowerPreferred < minFico;
        const borrowerDefaultQualifies = borrowerDefault > minFico;
        const coBorrowerPreferredQualifies = coBorrowerPreferred > minFico;
    
        // Only preferred row Red if Borrower preferred fails & co-borrower preferred qualifies
        if (scoreType === 'preferred' && borrowerPreferredFails && coBorrowerPreferredQualifies) {
          return extraColor.palette.error.secondary;
        }
    
        // Only default row Orange if Borrower preferred missing & default qualifies
        if (scoreType === 'default' && !borrowerPreferred && borrowerDefaultQualifies) {
          return colorTheme.palette.orange[200];
        }
    
        return '';
      },
      []
    );

    const handleView = (isBorrower: boolean, bureauDetails?: Partial<IPrimaryBorrowerBureau>) => {
      if (!bureauDetails) return;

      if (isBorrower) {
        setContactId(contactIdQuery);
      } else {
        setContactId(coContactId ?? '');
      }
      const mapping = getBureauMapping(bureauDetails.preferredBureau);
      if (!mapping) {
        setPulledFicoTypes([]);
        setSelectedBureauType(null); // explicitly clear
        setDisplayCreditReport(false);
        return;
      }
    
      const { bureauType, ficoType } = mapping;
      setSelectedBureauType(bureauType);
      setPulledFicoTypes([ficoType]);
    
      // Open modal after state has been set
      setTimeout(() => setDisplayCreditReport(true), 0); 
      onOpenCreditReport(bureauType, [ficoType]);
    };

    const onOpenCreditReport = useCallback(
        async (bureauType: EBureauType | null, experianFicoTypeArray: CreditBureauScoreType[]) => {
          if (!bureauType) {
            return;
          }
          if (
            ((bureauType === EBureauType.Experian || bureauType === EBureauType.ExperianFico) && canPullExperian) ||
            (bureauType === EBureauType.Transunion && canPullTransunion) ||
            (bureauType === EBureauType.Equifax && canPullEquifax) ||
            canPullAll ||
            canViewAll
          ) {
            setCreditReports((prevState) => ({
              ...prevState,
              [bureauType]: (prevState[bureauType] || 0) + 1,
            }));
            setPulledFicoTypes(experianFicoTypeArray);
            reloadContactAlerts();
            return;
          }
          Notistack.generalError('You don\'t have permissions to view credit reports.');
        },
        [canPullAll, canPullExperian, canPullTransunion, canPullEquifax, canViewAll]
      );
      
    const onCloseCreditReport = useCallback((bureauType: string) => {
      setCreditReports((prevState) => {
        const newState = { ...prevState };
        delete newState[bureauType];
        return newState;
      });
      setDisplayCreditReport(false);
    }, []);

    const renderScore = (score?: number) => (
      <span
        style={{
          display: 'inline-block',
          width: '3ch',// exactly 3 character width
          textAlign: 'right',
        }}
      >
        {score ?? ' '}
      </span>
    );

  return (
    <>
      <LenderSectionRow>{formatCurrency(valuation)}</LenderSectionRow>
      <ValuationBlock
        defaultValuationMethods={lender.defaultValuationMethods}
        isExpanded={isExpanded}
      />

      <LenderSectionRowValidation
        style={{
          backgroundColor: getBorrowerCreditBgColor(lender, 'default'),
        }}
      >
          {lender.primaryBorrowerBureau && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              {renderScore(lender.primaryBorrowerBureau?.defaultScore)}
              <span>
                {lender.primaryBorrowerBureau?.defaultBureau
                  ? CreditBureauScoreTypeMap[
                      lender.primaryBorrowerBureau.defaultBureau as keyof typeof CreditBureauScoreTypeMap
                    ]
                  : ''}
              </span>
            </Box>
          )}
      </LenderSectionRowValidation>
      <LenderSectionRowValidation
        style={{
          backgroundColor: getBorrowerCreditBgColor(lender, 'preferred'),
        }}
      >
        {lender.primaryBorrowerBureau?.preferredBureau && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
                flex: 1,
              }}
            >
              {renderScore(lender.primaryBorrowerBureau?.preferredScore)}

              <Box
                component="span"
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {CreditBureauScoreTypeMap[
                  lender.primaryBorrowerBureau.preferredBureau as keyof typeof CreditBureauScoreTypeMap
                ] ?? ''}
              </Box>
            </Box>

            <Button
              variant="contained"
              size="small"
              sx={{
                ml: 'auto',
                flexShrink: 0,
                minHeight: '2.25rem',
              }}
              onClick={() =>
                handleView(true, lender.primaryBorrowerBureau)
              }
            >
              View
            </Button>
          </Box>
        )}
      </LenderSectionRowValidation>
      <LenderSectionRowValidation>
          <LenderSectionRow>
            {lender.coBorrowerBureau && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                {renderScore(lender.coBorrowerBureau?.defaultScore)}
                <span>
                  {lender.coBorrowerBureau?.defaultBureau
                    ? CreditBureauScoreTypeMap[
                        lender.coBorrowerBureau.defaultBureau as keyof typeof CreditBureauScoreTypeMap
                      ]
                    : ''}
                </span>
              </Box>
            )}
          </LenderSectionRow>
      </LenderSectionRowValidation>
      <LenderSectionRowValidation>
        {lender.coBorrowerBureau?.preferredBureau && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
                flex: 1,
              }}
            >
              {renderScore(lender.coBorrowerBureau?.preferredScore)}

              <Box
                component="span"
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {CreditBureauScoreTypeMap[
                  lender.coBorrowerBureau.preferredBureau as keyof typeof CreditBureauScoreTypeMap
                ] ?? ''}
              </Box>
            </Box>

            <Button
              variant="contained"
              size="small"
              disabled={!coContactId}
              sx={{
                ml: 'auto',
                flexShrink: 0,
                minHeight: '2.25rem',
              }}
              onClick={() => handleView(false, lender.coBorrowerBureau)}
            >
              View
            </Button>
          </Box>
        )}
      </LenderSectionRowValidation>
      <LenderSectionRow>{formatCurrency(lender?.maxCashOutYellow || lender?.maxCashOutGreen)}</LenderSectionRow>
      <LenderSectionRow>
        <FastField
          prefix="$"
          name={`eligibleLenders[${idx}].inputs.cashOut`}
          component={NumberInputField}
          sx={{
            '& input': {
              backgroundColor: getColorCoding(
                lender.inputs.cashOut || 0,
                lender.maxCashOutYellow || 0,
                lender.maxCashOutGreen || 0,
              ),
            },
          }}
        />
      </LenderSectionRow>
      <LenderSectionRow>{formatPercentage(maxLtvGreen)}</LenderSectionRow>
      <LenderSectionRowValidation
        style={{
          backgroundColor: getColorCoding(LTV(), maxLtvGreen, maxLtvYellow),
        }}
      >
        {formatPercentage(LTV() || 0)}
      </LenderSectionRowValidation>
      <LenderSectionRow>
        <b>{formatCurrency(Math.max(lender.autoSubmitSelectedTierCondition?.requiredDownPaymentGreen || 0, 0))}</b>
      </LenderSectionRow>
      <LenderSectionRow>
        <FastField
          decimalScale={2}
          prefix="$"
          name={`eligibleLenders[${idx}].inputs.downPayment`}
          component={NumberInputField}
          sx={{
            '& input': {
              backgroundColor: getDownPaymentColorCoding(
                lender.inputs.downPayment || 0,
                Math.max(lender.autoSubmitSelectedTierCondition?.requiredDownPaymentGreen || 0, 0),
              ),
            },
          }}
        />
      </LenderSectionRow>
      <LenderSectionRow>{tierConditionsFirstTermGroup}</LenderSectionRow>
      <LenderSectionRow>
        <FastField
          name={`eligibleLenders[${idx}].inputs.term`}
          component={NumberInputField}
          limit={3}
          sx={{
            '& input': {
              backgroundColor: maxTermRed(),
            },
          }}
        />
      </LenderSectionRow>
      {selectedBureauType && (
        <Modal
          draggable
          hideBackdrop={false}
          open={displayCreditReport}
          title={selectedBureauType}
          dialogWidth="42%"
          dialogHeight="95%"
          resetPosition={creditReports[selectedBureauType] || 0}
          onClose={() => onCloseCreditReport(selectedBureauType)}
        >
          <DetailedCreditReport
            bureauType={selectedBureauType}
            contactId={contactId}
            reloadContact={reloadContact}
            pulledFicoTypes={pulledFicoTypes}
            isViewOnly={true}
            reloadDealCalculator={reloadDealCalculator}
          />
        </Modal>
    )}
    </>
  );
};

export default LenderInputs;
